import type { RatingPeriodBreakdown } from '@brush/api-client';
import {
  Spinner,
  decayedFigure,
  periodCutLabel,
  periodRangeLabel,
  periodWeightLabel,
  ratingPeriodView,
  scoredNothingNote,
  tailDroppedNote,
} from '@brush/ui';
import { BreakdownEventCard, formatPoints } from './BreakdownEventCard';
import { usePlayer } from '../player';
import { useRatingBreakdown } from '../queries';

/**
 * §7, on the player's side of the glass: why they have the rating they have.
 *
 * <b>A rating adds two reasons a number is not what somebody expects, and both are why this exists.</b>
 * A leaderboard breakdown answers "what was each finish worth". A rating has to answer that, and then
 * "which of my results counted" and "what did age do to them" — so this is grouped by period, each
 * period stating its own dates, its cut and its weight, with the results inside it best raw score
 * first. That is §3.3's own selection order, so the cut line is visible where it falls.
 *
 * <b>A result the cut dropped is HERE, dimmed, with the server's reason.</b> §7 is explicit and the
 * reason is worth keeping in front of whoever edits this: a player who cashed and sees no trace of it
 * will report a bug, and the obvious reading of an absent event is that the room never recorded the
 * finish.
 *
 * <b>Every event card is the same component the leaderboard breakdown renders</b>, with the same
 * server-computed figures and the same sentence. §7 forbids re-deriving, reshaping or summarising that
 * object; the fantasy F6 defect was a label reading the wrong field, and the fix rule was one pure
 * function both call sites use. What this file adds sits <i>around</i> it — the period, the decayed
 * figure, the selection note — and never inside.
 */
export function RatingBreakdown({
  boardId,
  sequenceNumber,
  playerId,
}: {
  boardId: string;

  /**
   * The snapshot the standings above came from, and part of the cache key — which is what makes
   * "fetched once per player" structural. A recalculation is a new sequence number and therefore a
   * new key, so this answer can be held forever without ever going stale.
   */
  sequenceNumber: number;

  playerId: string;
}) {
  const { theme } = usePlayer();
  const query = useRatingBreakdown(boardId, sequenceNumber, playerId);

  if (query.isLoading) return <Spinner label="Working out the rating…" />;

  if (query.isError || !query.data) {
    return (
      <p role="alert" className="px-5 py-4 text-sm" style={{ color: theme.textMuted }}>
        Couldn’t load your rating breakdown just now.
      </p>
    );
  }

  const breakdown = query.data;
  const places = breakdown.decimalPlaces;

  if (breakdown.periods.length === 0) {
    return (
      <p className="px-5 py-4 text-sm" style={{ color: theme.textMuted }}>
        {/*
         * Three absences and three sentences, because they are three different things to be told.
         * One says come back later, one says the room's rules leave you out, one says you have no
         * results in what it scores — and a single "nothing to show" says none of them.
         */}
        {breakdown.sequenceNumber === null
          ? 'Your room hasn’t worked this rating out yet, so there’s nothing to break down.'
          : breakdown.excludedFromCohort
            ? 'This rating covers a particular group of players, and your card isn’t in it — so it doesn’t score you.'
            : 'None of the events this rating counts has a result for you.'}
      </p>
    );
  }

  const tail = tailDroppedNote(breakdown.tailDropped);

  return (
    <div className="space-y-3 px-5 pb-5 pt-1">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm font-bold">
          {formatPoints(breakdown.displayRating, places)}
        </span>
        <span className="text-sm" style={{ color: theme.textMuted }}>
          from {breakdown.eventsSelected} of {breakdown.eventsEntered} event
          {breakdown.eventsEntered === 1 ? '' : 's'}, over {breakdown.periods.length} time
          {breakdown.periods.length === 1 ? ' period' : ' periods'}
        </span>
      </header>

      {/* The server's own sentence, verbatim — the same rule that keeps the engine's explanations
          off the client. */}
      {breakdown.isQualified || breakdown.qualificationShortfall === null ? null : (
        <p className="text-xs" style={{ color: theme.textMuted }}>
          Provisional — {breakdown.qualificationShortfall}.
        </p>
      )}

      {tail === null ? null : (
        <p className="text-xs" style={{ color: theme.textMuted }}>
          {tail}
        </p>
      )}

      <ol className="space-y-4">
        {breakdown.periods.map((period) => (
          <Period key={period.index} period={period} places={places} />
        ))}
      </ol>

      {/*
       * §2.2's anchor, and §8's "these are a snapshot". A rating moves when the calendar moves, so
       * the date it was measured as of is the one fact the figures above are meaningless without —
       * and a player who has just cashed and cannot find it here needs to know the rating has not
       * been worked out since, rather than concluding their result was lost.
       */}
      <p className="text-xs" style={{ color: theme.textMuted }}>
        Measured as of {breakdown.asOfDate}
        {breakdown.isFinal ? '. These are the final standings.' : '.'}
      </p>
    </div>
  );
}

/**
 * One period: the dates it covers, what counts inside it, and what age does to what does.
 *
 * <b>The dates are why this is grouped rather than listed flat.</b> §7 asks for them so a reader can
 * check the bucketing without knowing the anchor arithmetic — "why is this in the older half" is a
 * question only these two dates answer. The `from` the server sends is already the first <i>included</i>
 * date, not the exclusive edge the arithmetic uses.
 */
function Period({ period, places }: { period: RatingPeriodBreakdown; places: number }) {
  const { theme } = usePlayer();
  const view = ratingPeriodView(period);
  const collapsed = scoredNothingNote(view.scoredNothing);

  return (
    <li className="space-y-2">
      <div
        className="flex flex-wrap items-baseline gap-x-2 gap-y-1 pb-1 text-xs"
        style={{ color: theme.textMuted, borderBottom: `1px solid ${theme.border}` }}
      >
        <span className="font-bold uppercase tracking-wider">Period {period.index}</span>
        <span>{periodRangeLabel(period.from, period.to)}</span>
        <span>·</span>
        <span>{periodCutLabel(period.n)}</span>
        <span>·</span>
        <span>{periodWeightLabel(period.decay)}</span>
        <span className="ml-auto font-bold" style={{ color: theme.text }}>
          {formatPoints(period.contribution, places)}
        </span>
      </div>

      {view.rows.length === 0 ? null : (
        <ul className="space-y-2">
          {view.rows.map((result) => (
            <BreakdownEventCard
              key={result.eventId}
              event={result.breakdown}
              places={places}
              // §3.3's answer, not §3.9's: `counted` is true on every one of these rows, so the card's
              // own dimming never fires here and this is what marks a result the cut dropped.
              dimmed={!result.selected}
            >
              <RatingRow result={result} places={places} />
            </BreakdownEventCard>
          ))}
        </ul>
      )}

      {collapsed === null ? null : (
        <p className="text-xs" style={{ color: theme.textMuted }}>
          {collapsed}
        </p>
      )}
    </li>
  );
}

/**
 * What the rating layer adds to the engine's own working: what this result actually contributed to
 * the total, and why, when the two differ.
 *
 * <b>§7 puts the decayed score ON THE ROW, and this is that.</b> "Why did this contribute 45 instead
 * of 50" is answered where it is asked. It is withheld where nothing happened to the row: a result
 * whose contribution is its score has neither aged nor been cut, and `50 → 50` spends a reader's
 * attention on the case that needs none — the same omission the engine already makes for a multiplier
 * of exactly 1.
 */
function RatingRow({
  result,
  places,
}: {
  result: RatingPeriodBreakdown['results'][number];
  places: number;
}) {
  const { theme } = usePlayer();
  const decayed = decayedFigure(result);

  if (decayed === null && result.selectionNote === null) return null;

  return (
    <div
      className="mt-2 space-y-0.5 pt-2 text-xs"
      style={{ borderTop: `1px solid ${theme.border}`, color: theme.textMuted }}
    >
      {decayed === null ? null : (
        <p>
          {result.selected
            /* The clause is the point, and room-web deliberately omits it. A player has never seen
               the decay ramp and cannot discover it, so a bare pair of numbers reads as an error in
               their favour that somebody took back. See `decayedFigure` in @brush/ui. */
            ? `Counted ${formatPoints(decayed, places)} of ${formatPoints(result.eventScore, places)} — this period is worth less because it is older.`
            : `Counted nothing towards your rating, so ${formatPoints(result.eventScore, places)} of it did not reach the total.`}
        </p>
      )}

      {/* The server's sentence, verbatim. §3.3 writes two of them and they answer different
          questions: one says you were beaten, the other that there was nothing to beat. */}
      {result.selectionNote === null ? null : <p>{result.selectionNote}</p>}
    </div>
  );
}
